// src/components/programStaff/MyTaskCalendar.jsx

import React, { useState, useEffect } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { getAuthHeaders } from "../../api/projectCreationApi";
import axios from "axios";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { Dialog } from "@headlessui/react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const locales = {
    "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

const API_BASE_URL =
    process.env.REACT_APP_BACKEND_URL ||
    "https://ims.holdthechild.net" ||
    "https://ims.holdthechild.net";

const getMyAssignedTasks = async () => {
    const headers = getAuthHeaders();
    if (!headers || !headers.Authorization) {
        console.error("No authorization token found.");
        return [];
    }
    const response = await axios.get(
        `${API_BASE_URL}/api/projects/tasks/my`,
        { headers: headers }
    );
    return response.data.data;
};

const CustomToolbar = ({ label, onNavigate, onView, view }) => (
    <div className="flex items-center justify-between px-4 py-2 border-b bg-white">
        <div className="flex items-center gap-2">
            <button
                onClick={() => onNavigate("TODAY")}
                className="px-3 py-1 rounded-md border text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
                Today
            </button>
            <button
                onClick={() => onNavigate("PREV")}
                className="p-1 rounded-md hover:bg-gray-100"
            >
                <ChevronLeft size={18} />
            </button>
            <button
                onClick={() => onNavigate("NEXT")}
                className="p-1 rounded-md hover:bg-gray-100"
            >
                <ChevronRight size={18} />
            </button>
            <span className="ml-4 text-lg font-semibold text-gray-800">{label}</span>
        </div>
        <div className="flex gap-2">
            {["month", "week", "day"].map((v) => (
                <button
                    key={v}
                    onClick={() => onView(v)}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${view === v
                        ? "bg-orange-600 text-white"
                        : "text-gray-700 hover:bg-gray-100"
                        }`}
                >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
            ))}
        </div>
    </div>
);

const MyTaskCalendar = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);

    useEffect(() => {
        const fetchTasks = async () => {
            try {
                const fetchedEvents = await getMyAssignedTasks();
                const now = new Date();
                const formattedEvents = fetchedEvents
                    .map((event) => {
                        const startDate = event.start ? new Date(event.start) : null;
                        const endDate = event.end ? new Date(event.end) : null;
                        const normalizedStatus = event.status ? event.status.trim().toLowerCase() : "";
                        const isOverdue = endDate && endDate < now && normalizedStatus !== "completed";
                        return { ...event, start: startDate, end: endDate, isOverdue, status: event.status };
                    })
                    .filter(
                        (event) =>
                            event.start && event.end && !isNaN(event.start) && !isNaN(event.end)
                    );
                setEvents(formattedEvents);
            } catch (err) {
                console.error("Failed to fetch assigned tasks:", err);
                setError("Failed to load tasks. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        fetchTasks();
    }, []);

    const CustomEvent = ({ event }) => (
        <div
            className="truncate cursor-pointer overflow-visible"
            data-tooltip-id="calendar-tooltip"
            data-tooltip-html={`
                <div style="font-size:14px; line-height:1.4;">
                  ${event.isOverdue ? '<p style="color:red; font-weight:bold; margin-bottom: 6px;">⚠️ Overdue!</p>' : ''}
                  <strong style="font-size:15px;">${event.title}</strong>
                  <div style="margin-top:6px;">
                    ${event.projectTitle ? `<p><strong>Project:</strong> ${event.projectTitle}</p>` : ''}
                    ${event.activityTitle ? `<p><strong>Activity:</strong> ${event.activityTitle || "N/A"}</p>` : ''}
                    <p><strong>Status:</strong> ${event.status}</p>
                  </div>
                </div>
            `}
        >
            {event.title}
        </div>
    );

    const eventPropGetter = (event) => {
        let baseStyle = {
            borderRadius: "8px",
            padding: "2px 6px",
            fontSize: "13px",
            fontWeight: 500,
            color: "white",
            overflow: "visible",
            border: '2px solid transparent',
            backgroundColor: "#3b82f6", // Default task color
        };

        if (event.isOverdue) {
            baseStyle.border = '2px solid #ef4444';
            baseStyle.backgroundColor = '#fca5a5';
            baseStyle.color = '#7f1d1d';
            baseStyle.fontWeight = 700;
        }

        if (event.status && event.status.trim().toLowerCase() === "completed") {
            baseStyle.backgroundColor = "#10b981"; // Green for completed
            baseStyle.border = '2px solid transparent';
            baseStyle.color = 'white';
        }

        return { style: baseStyle };
    };

    if (loading)
        return <div className="text-center py-10 text-gray-500">Loading your tasks...</div>;
    if (error)
        return <div className="text-center py-10 text-red-500 font-medium">{error}</div>;
    if (events.length === 0)
        return (
            <div className="text-center py-10 text-gray-500">
                No tasks found for you.
            </div>
        );

    return (
        <div className="w-full">
            <div className="flex gap-4 items-center mb-4 text-sm">
                <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-blue-500"></span> Task
                </div>
                <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span> Completed
                </div>
                <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full border-2 border-red-500 bg-red-300"></span> Overdue
                </div>
            </div>

            <div className="h-[650px] rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: "100%" }}
                    components={{ event: CustomEvent, toolbar: CustomToolbar }}
                    eventPropGetter={eventPropGetter}
                    views={["month", "week", "day"]}
                    defaultView="month"
                    popup
                    onSelectEvent={(event) => setSelectedEvent(event)}
                />
            </div>

            <ReactTooltip
                id="calendar-tooltip"
                place="top"
                effect="solid"
                className="z-50"
                style={{
                    backgroundColor: "#111827",
                    color: "#F9FAFB",
                    borderRadius: "10px",
                    padding: "12px",
                    maxWidth: "280px",
                }}
            />

            <Dialog
                open={!!selectedEvent}
                onClose={() => setSelectedEvent(null)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
                        {selectedEvent && (
                            <>
                                <Dialog.Title className="text-lg font-semibold mb-2 flex items-center gap-2">
                                    {selectedEvent.isOverdue && <span className="text-red-500">⚠️</span>}
                                    {selectedEvent.title}
                                </Dialog.Title>
                                <div className="space-y-1 text-sm text-gray-700">
                                    {selectedEvent.projectTitle && (
                                        <p><strong>Project:</strong> {selectedEvent.projectTitle}</p>
                                    )}
                                    <p><strong>Type:</strong> {selectedEvent.type}</p>
                                    {selectedEvent.activityTitle && (
                                        <p><strong>Activity:</strong> {selectedEvent.activityTitle}</p>
                                    )}
                                    <p><strong>Status:</strong> {selectedEvent.status}</p>
                                    <p><strong>End:</strong> {selectedEvent.end?.toLocaleString()}</p>
                                </div>
                                <div className="mt-4 flex justify-end">
                                    <button
                                        onClick={() => setSelectedEvent(null)}
                                        className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                                    >
                                        Close
                                    </button>
                                </div>
                            </>
                        )}
                    </Dialog.Panel>
                </div>
            </Dialog>
        </div>
    );
};

export default MyTaskCalendar;
